import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@mui/styles";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CheckIcon from "@mui/icons-material/Check";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import GroupIcon from "@mui/icons-material/Group";
import BlockIcon from "@mui/icons-material/Block";
import OutlinedFlagIcon from "@mui/icons-material/OutlinedFlag";
import MilitaryTechOutlinedIcon from "@mui/icons-material/MilitaryTechOutlined";
import FlashOnOutlinedIcon from "@mui/icons-material/FlashOnOutlined";
import WhatshotOutlinedIcon from "@mui/icons-material/WhatshotOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import utils from "../../universal/utils";
import {
  pickAvatarUpdatedAtMs,
  resolveAvatarSrc,
} from "../../utils/avatarUrl";
import Rating3Badge from "../common/Rating3Badge";
import ImpactRatingBadge from "../common/ImpactRatingBadge";
import EloBadge from "../common/EloBadge.js";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import {
  buildMatchPlayerCardData,
  getMatchPlayerCardMetricGroups,
} from "./matchPlayerCardData";
import { FOUNDER_BADGE, FOUNDER_TOOLTIP_TEXT } from "../../constants/founder";

const compactSkillChipIcon = {
  fontSize: "0.72rem",
  marginLeft: "0.18rem",
  marginRight: "-0.02rem",
  marginTop: "0.01rem",
  color: "inherit",
};

const compactSkillChipLabel = {
  padding: "0 0.28rem",
};

const styles = (theme) => ({
  card: {
    alignSelf: "stretch",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    borderRadius: 8,
    backgroundColor: "#2A2F34",
    padding: "0.5rem 0.6rem",
    boxSizing: "border-box",
    transition: "background-color 0.15s ease-in-out",
    "&:hover": {
      backgroundColor: "#2F3439",
    },
  },

  partyBar: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: 4,
    borderTopLeftRadius: 8,
    borderBottomLeftRadius: 8,
    pointerEvents: "none",
  },

  headerRow: {
    display: "flex",
    alignItems: "center",
    gap: "0.4rem",
    minWidth: 0,
    marginBottom: "0.08rem",
  },
  avatar: {
    width: "2rem",
    height: "2rem",
    flexShrink: 0,
  },
  playerName: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "#ffffff",
    fontSize: "0.88rem",
    fontWeight: 600,
    lineHeight: 1.2,
  },
  headerFounderBadge: {
    height: "1.12rem",
    fontSize: "0.54rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    backgroundColor: FOUNDER_BADGE.bgColor,
    color: FOUNDER_BADGE.color,
    border: `1px solid ${FOUNDER_BADGE.borderColor}`,
    flexShrink: 0,
    marginLeft: "-0.04rem",
    "& .MuiChip-label": {
      padding: "0 0.34rem",
      display: "flex",
      alignItems: "center",
      lineHeight: 1,
    },
    "& .MuiChip-icon": {
      fontSize: "0.72rem",
      marginLeft: "0.2rem",
      marginRight: "-0.04rem",
      color: "inherit",
    },
  },
  headerVerifiedBadge: {
    height: "1.12rem",
    fontSize: "0.54rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    backgroundColor: "rgba(56, 176, 0, 0.14)",
    color: "#6BD63C",
    border: "1px solid rgba(56, 176, 0, 0.58)",
    flexShrink: 0,
    marginLeft: "-0.04rem",
    "& .MuiChip-label": {
      padding: "0 0.32rem",
      display: "flex",
      alignItems: "center",
      lineHeight: 1,
    },
    "& .MuiChip-icon": {
      fontSize: "0.88rem",
      marginLeft: "0.18rem",
      marginRight: "-0.08rem",
      color: "inherit",
    },
  },
  playerCountryFlag: {
    height: "1.16rem",
    borderRadius: 3,
    flexShrink: 0,
  },
  menuButton: {
    color: "#ffffff",
    padding: "0.15rem",
    flexShrink: 0,
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    },
  },

  infoRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "0.24rem",
    marginTop: "0.34rem",
    minHeight: 0,
  },
  founderBadge: {
    height: "1.15rem",
    fontSize: "0.5rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    backgroundColor: FOUNDER_BADGE.bgColor,
    color: FOUNDER_BADGE.color,
    border: `1px solid ${FOUNDER_BADGE.borderColor}`,
    "& .MuiChip-label": {
      padding: "0 0.3rem",
    },
  },
  membershipBadge: {
    height: "1.15rem",
    fontSize: "0.5rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    "& .MuiChip-label": {
      padding: "0 0.3rem",
    },
  },
  infoSeparator: {
    color: "#555",
    fontSize: "0.52rem",
    userSelect: "none",
  },
  infoLabel: {
    color: "#8C939A",
    fontSize: "0.64rem",
    fontWeight: 700,
    letterSpacing: "0.03em",
    textTransform: "uppercase",
    lineHeight: 1,
  },
  infoValue: {
    color: "#ffffff",
    fontSize: "0.86rem",
    fontWeight: 700,
    lineHeight: 1,
    whiteSpace: "nowrap",
  },
  infoGroup: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.16rem",
  },
  statsRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: "0.08rem",
    marginTop: "0.42rem",
    paddingTop: "0.38rem",
    borderTop: "1px solid rgba(255,255,255,0.06)",
  },
  statCell: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    minWidth: 0,
    flex: 1,
    overflow: "hidden",
  },
  statValue: {
    color: "#ffffff",
    fontSize: "0.86rem",
    fontWeight: 700,
    lineHeight: 1.1,
    whiteSpace: "nowrap",
  },
  statLabel: {
    color: "#6B7280",
    fontSize: "0.56rem",
    fontWeight: 600,
    letterSpacing: "0.015em",
    textTransform: "uppercase",
    lineHeight: 1,
    whiteSpace: "nowrap",
    marginTop: "0.12rem",
    textAlign: "center",
    maxWidth: "100%",
    overflow: "hidden",
  },

  csSkillsRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "0.26rem",
    marginTop: "0.42rem",
    minHeight: "1.24rem",
  },
  roleChip: {
    height: "1.22rem",
    fontSize: "0.62rem",
    fontWeight: 600,
    backgroundColor: "rgba(232, 163, 61, 0.2)",
    color: "#E8A33D",
    border: "1px solid rgba(232, 163, 61, 0.3)",
    "& .MuiChip-label": {
      ...compactSkillChipLabel,
      display: "flex",
      alignItems: "center",
      lineHeight: 1,
    },
    "& .MuiChip-icon": {
      ...compactSkillChipIcon,
    },
  },
  utilityChip: {
    height: "1.22rem",
    fontSize: "0.62rem",
    fontWeight: 500,
    backgroundColor: "rgba(56, 176, 0, 0.15)",
    color: "#6BD63C",
    border: "1px solid rgba(56, 176, 0, 0.25)",
    "& .MuiChip-label": {
      ...compactSkillChipLabel,
      display: "flex",
      alignItems: "center",
      lineHeight: 1,
    },
    "& .MuiChip-icon": {
      ...compactSkillChipIcon,
    },
  },
  mapChip: {
    height: "1.22rem",
    fontSize: "0.62rem",
    fontWeight: 600,
    backgroundColor: "rgba(76, 132, 255, 0.14)",
    color: "#8CB4FF",
    border: "1px solid rgba(76, 132, 255, 0.28)",
    "& .MuiChip-label": {
      ...compactSkillChipLabel,
      display: "flex",
      alignItems: "center",
      lineHeight: 1,
    },
    "& .MuiChip-icon": {
      ...compactSkillChipIcon,
    },
  },

  menu: {
    zIndex: 9999,
    "& .MuiPaper-root": {
      backgroundColor: "#0b0b0b",
      border: "1px solid #666666",
      borderRadius: "0.5rem",
      padding: "0.35rem",
      minWidth: "13.5rem",
      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
      transition: "all 0.3s ease",
    },
    "& .MuiList-root": {
      paddingTop: 0,
      paddingBottom: 0,
    },
  },
  menuItem: {
    color: "#ffffff",
    padding: "0.35rem 0.75rem",
    borderRadius: "0.35rem",
    transition: "all 200ms ease-in-out",
    display: "flex",
    alignItems: "center",
    "&:hover": {
      backgroundColor: "#1a1a1a",
    },
  },
  menuItemDisabled: {
    opacity: 0.5,
    color: "#999999",
  },
  menuIcon: {
    minWidth: "2rem",
    color: "#ffffff",
    "& .MuiSvgIcon-root": {
      fontSize: "1.15rem",
    },
  },
  menuText: {
    margin: 0,
    "& .MuiTypography-root": {
      fontSize: "0.82rem",
      fontWeight: 500,
      lineHeight: 1.2,
      color: "#ffffff",
    },
  },
  menuDivider: {
    margin: "0.3rem 0",
    borderColor: "#333333",
  },
});


const ROLE_DISPLAY = {
  igl: "IGL",
  entryFragger: "Entry",
  awper: "AWP",
  support: "Support",
  lurker: "Lurker",
};

const UTILITY_SHORT = {
  smokeLineups: "Smokes",
  mollyLineups: "Mollies",
  flashLineups: "Flashes",
  nadeLineups: "Nades",
};

function hasMetricValue(value) {
  return value != null && value !== "";
}

function getNumericSkill(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatSkillScore(value) {
  if (!Number.isFinite(value)) return "0";
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

class MatchPlayerBox extends React.Component {
  render() {
    const {
      classes,
      player,
      currentUser,
      avatarCacheBust = 0,
      relationship,
      menuAnchor,
      menuAnchorSteamId,
      onMenuOpen,
      onMenuClose,
      onProfileClick,
      onAddFriend,
      onCancelFriendRequest,
      onReportClick,
      canReport,
      alreadyReported,
      viewMode = "hybrid",
      partyColor,
    } = this.props;

    const csSkills = player?.csSkills || null;
    const card = buildMatchPlayerCardData(player);
    const metricGroups = getMatchPlayerCardMetricGroups(card, viewMode);
    const allMetrics = [
      ...metricGroups.primary,
      ...metricGroups.secondary,
      ...(metricGroups.inline || []),
    ].filter((m) => hasMetricValue(m?.value));
    const summaryLabelStyle = {
      fontSize: "0.64rem",
      lineHeight: 1,
    };
    const summaryValueStyle = {
      fontSize: "0.86rem",
      lineHeight: 1,
      whiteSpace: "nowrap",
    };
    const statValueStyle = {
      fontSize: "0.86rem",
      lineHeight: 1.1,
      whiteSpace: "nowrap",
    };
    const statLabelStyle = {
      fontSize: "0.64rem",
      lineHeight: 1,
      whiteSpace: "nowrap",
      marginTop: "0.12rem",
    };

    const playerSteamIdRaw =
      player?.steamId || player?.steamId64 || player?.steam64Id || null;
    const currentUserSteamIdRaw =
      currentUser?.steamId || currentUser?.steamId64 || currentUser?.steam64Id || null;
    const playerSteamId =
      playerSteamIdRaw != null ? String(playerSteamIdRaw) : null;
    const currentUserSteamId =
      currentUserSteamIdRaw != null ? String(currentUserSteamIdRaw) : null;
    const isCurrentUser =
      (player?._id && currentUser?._id && player._id === currentUser._id) ||
      (player?.userId && currentUser?.userId && player.userId === currentUser.userId) ||
      (playerSteamId && currentUserSteamId && playerSteamId === currentUserSteamId);
    const canViewProfile = Boolean(player?._id);
    const canManageFriendship =
      currentUser?.accountStatus !== "banned" &&
      currentUser?.accountStatus !== "noteligible";

    const basePath = utils.getEdgeBasePath();
    const avatarVersion = pickAvatarUpdatedAtMs(
      isCurrentUser ? currentUser : null,
      player,
      isCurrentUser ? avatarCacheBust : null
    );
    const effectiveAvatarSrc = resolveAvatarSrc({
      basePath,
      picture: player?.picture,
      userId: (isCurrentUser ? currentUser?.userId : null) || player?.userId || player?._id,
      avatarUpdatedAtMs: avatarVersion,
      hasCustomAvatar: isCurrentUser ? !!currentUser?.avatar : !!player?.avatar,
    });

    const roles = csSkills?.roles || {};
    const topRole = Object.entries(roles).reduce(
      (best, [key, value]) =>
        value > (best?.score || 0) ? { key, score: value } : best,
      null
    );
    const utilities = csSkills?.utilities || {};
    const flashScore = getNumericSkill(utilities.flashLineups);
    const smokeScore = getNumericSkill(utilities.smokeLineups);
    const mollyScore = getNumericSkill(utilities.mollyLineups);
    const nadeScore = getNumericSkill(utilities.nadeLineups);
    const flashSmokeScore = (flashScore + smokeScore) / 2;
    const mollyNadeScore = (mollyScore + nadeScore) / 2;
    const stratKnowledge = Number(csSkills?.stratKnowledge || 0);
    const mapProficiencyLabel = `Map ${Math.round(stratKnowledge)}/10`;
    const hasRoleChip = Boolean(csSkills && topRole && topRole.score > 0);
    const hasFlashSmokeChip = flashSmokeScore > 0;
    const hasMollyNadeChip = mollyNadeScore > 0;
    const hasMapChip = stratKnowledge > 0;
    const roleChipLabel = hasRoleChip
      ? `${ROLE_DISPLAY[topRole.key] || topRole.key} ${topRole.score}`
      : "";
    const csSkillsRowStyle = {
      marginTop: "0.42rem",
    };

    const reputationSummary = [card.reputation?.scoreText, card.reputation?.label]
      .filter(Boolean)
      .join(" · ");

    const hasInfoRow =
      card.membership.badgeLabel ||
      card.elo.display ||
      card.rating3.value != null ||
      player?.impactRating?.current != null ||
      reputationSummary;

    return (
      <Paper
        elevation={0}
        className={classes.card}
        style={{
          border: isCurrentUser ? "solid 2px #E8A33D" : "none",
        }}
      >
        {partyColor ? (
          <div className={classes.partyBar} style={{ backgroundColor: partyColor }} />
        ) : null}
        {/* Row 1: Avatar + Name + Flag + Menu */}
        <div className={classes.headerRow}>
          <Avatar className={classes.avatar} src={effectiveAvatarSrc} />
          <Typography component="div" className={classes.playerName}>
            {card.identity.displayName}
          </Typography>
          {card.founderBadge ? (
            <Tooltip title={FOUNDER_TOOLTIP_TEXT} placement="top" arrow>
              <Chip
                className={classes.headerFounderBadge}
                icon={<WorkspacePremiumOutlinedIcon />}
                label={`F#${card.founderBadge.number || ""}`}
                size="small"
              />
            </Tooltip>
          ) : null}
          {card.verification.isVerified ? (
            <Tooltip title="This player completed biometric identity verification." placement="top" arrow>
              <Chip
                className={classes.headerVerifiedBadge}
                icon={<FingerprintIcon />}
                label="Verified"
                size="small"
              />
            </Tooltip>
          ) : null}
          {player.flagImageUrl ? (
            <img
              className={classes.playerCountryFlag}
              src={player.flagImageUrl}
              alt={`${player.name || card.identity.displayName}'s flag`}
            />
          ) : null}
          {!isCurrentUser && playerSteamId && (
            <>
              <IconButton
                className={classes.menuButton}
                onClick={onMenuOpen}
                size="small"
                data-steamid={playerSteamId}
              >
                <MoreVertIcon style={{ fontSize: "1rem" }} />
              </IconButton>
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor) && menuAnchorSteamId === playerSteamId}
                onClose={onMenuClose}
                className={classes.menu}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                {canViewProfile ? (
                  <MenuItem
                    className={classes.menuItem}
                    onClick={() => onProfileClick(player._id)}
                  >
                    <ListItemIcon className={classes.menuIcon}>
                      <PersonOutlineIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText className={classes.menuText} primary="View Profile" />
                  </MenuItem>
                ) : null}
                {canViewProfile && (canManageFriendship || canReport) ? (
                  <Divider className={classes.menuDivider} />
                ) : null}
                {canManageFriendship ? (
                  relationship === "friends" ? (
                    <MenuItem
                      className={`${classes.menuItem} ${classes.menuItemDisabled}`}
                      disabled
                    >
                      <ListItemIcon className={classes.menuIcon}>
                        <GroupIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText className={classes.menuText} primary="Friends" />
                    </MenuItem>
                  ) : relationship === "pending" ? (
                    <MenuItem
                      className={classes.menuItem}
                      onClick={() => {
                        if (onCancelFriendRequest) {
                          onCancelFriendRequest(playerSteamId);
                        }
                      }}
                    >
                      <ListItemIcon className={classes.menuIcon}>
                        <BlockIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        className={classes.menuText}
                        primary="Cancel friend request"
                      />
                    </MenuItem>
                  ) : (
                    <MenuItem
                      className={classes.menuItem}
                      onClick={() => onAddFriend(playerSteamId)}
                    >
                      <ListItemIcon className={classes.menuIcon}>
                        <PersonAddAlt1Icon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText className={classes.menuText} primary="Add Friend" />
                    </MenuItem>
                  )
                ) : null}
                {canReport ? (
                  <Divider className={classes.menuDivider} />
                ) : null}
                {canReport ? (
                  alreadyReported ? (
                    <MenuItem
                      className={`${classes.menuItem} ${classes.menuItemDisabled}`}
                      disabled
                    >
                      <ListItemIcon className={classes.menuIcon}>
                        <CheckIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText className={classes.menuText} primary="Reported" />
                    </MenuItem>
                  ) : (
                    <MenuItem
                      className={classes.menuItem}
                      onClick={() => {
                        onReportClick(playerSteamId, player.nickname);
                        onMenuClose();
                      }}
                    >
                      <ListItemIcon className={classes.menuIcon}>
                        <OutlinedFlagIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText className={classes.menuText} primary="Report" />
                    </MenuItem>
                  )
                ) : null}
              </Menu>
            </>
          )}
        </div>

        {/* Row 2: Badges + ELO + Rating + Reputation */}
        {hasInfoRow ? (
          <div className={classes.infoRow}>
            {card.membership.badgeLabel ? (
              <Chip
                className={classes.membershipBadge}
                label={card.membership.badgeLabel}
                size="small"
                style={{
                  backgroundColor: `${card.membership.color}22`,
                  color: card.membership.color,
                  border: `1px solid ${card.membership.color}`,
                }}
              />
            ) : null}
            {card.elo.display ? (
              <>
                {(card.founderBadge || card.membership.badgeLabel) && (
                  <span className={classes.infoSeparator}>·</span>
                )}
                <EloBadge value={card.elo.value} size="small" />
              </>
            ) : null}
            {card.rating3.value != null ? (
              <>
                <span className={classes.infoSeparator}>·</span>
                <span className={classes.infoGroup}>
                  <Typography
                    component="span"
                    className={classes.infoLabel}
                    style={summaryLabelStyle}
                  >
                    R3
                  </Typography>
                  <Rating3Badge
                    value={card.rating3.value}
                    subRatings={card.rating3.subRatings}
                    size="medium"
                    compact
                    valueStyle={summaryValueStyle}
                  />
                </span>
              </>
            ) : null}
            {player?.impactRating?.current != null ? (
              <>
                <span className={classes.infoSeparator}>·</span>
                <span className={classes.infoGroup}>
                  <Typography
                    component="span"
                    className={classes.infoLabel}
                    style={summaryLabelStyle}
                  >
                    IMP
                  </Typography>
                  <ImpactRatingBadge
                    value={player.impactRating.current}
                    components={player.impactRating?.components || null}
                    size="medium"
                    compact
                    valueStyle={summaryValueStyle}
                  />
                </span>
              </>
            ) : null}
            {reputationSummary ? (
              <>
                <span className={classes.infoSeparator}>·</span>
                <span className={classes.infoGroup}>
                  <Typography
                    component="span"
                    className={classes.infoLabel}
                    style={summaryLabelStyle}
                  >
                    Rep
                  </Typography>
                  <Typography
                    component="span"
                    className={classes.infoValue}
                    style={{
                      fontSize: "0.86rem",
                      lineHeight: 1,
                      whiteSpace: "nowrap",
                      color: card.reputation?.color || "#ffffff",
                    }}
                  >
                    {reputationSummary}
                  </Typography>
                </span>
              </>
            ) : null}
          </div>
        ) : null}

        {/* CS Skills chips - always reserve this row so both cards align */}
        <div className={classes.csSkillsRow} style={csSkillsRowStyle}>
          {hasRoleChip ? (
            <Tooltip title="Highest-rated role on this map from CS Skills" placement="top" arrow>
              <Chip
                className={classes.roleChip}
                icon={<MilitaryTechOutlinedIcon />}
                label={roleChipLabel}
                size="small"
              />
            </Tooltip>
          ) : null}
          {hasFlashSmokeChip ? (
            <Tooltip
            title="Flash + smoke utility score on this map from CS Skills"
            placement="top"
            arrow
          >
            <Chip
              className={classes.utilityChip}
              icon={<FlashOnOutlinedIcon />}
              label={`Flash/Smoke ${formatSkillScore(flashSmokeScore)}`}
              size="small"
            />
            </Tooltip>
          ) : null}
          {hasMollyNadeChip ? (
            <Tooltip
            title="Molly + nade utility score on this map from CS Skills"
            placement="top"
            arrow
          >
            <Chip
              className={classes.utilityChip}
              icon={<WhatshotOutlinedIcon />}
              label={`Molly/Nade ${formatSkillScore(mollyNadeScore)}`}
              size="small"
            />
            </Tooltip>
          ) : null}
          {hasMapChip ? (
            <Tooltip
            title="Current-map strategic knowledge from CS Skills (0-10)"
            placement="top"
            arrow
          >
            <Chip
              className={classes.mapChip}
              icon={<MapOutlinedIcon />}
              label={mapProficiencyLabel}
              size="small"
            />
            </Tooltip>
          ) : null}
        </div>

        {/* Row 3: Stats - single horizontal row, FACEIT-style */}
        {allMetrics.length > 0 ? (
          <div className={classes.statsRow}>
            {allMetrics.map((metric) => (
              <div key={metric.label} className={classes.statCell}>
                <Typography
                  component="div"
                  className={classes.statValue}
                  style={statValueStyle}
                >
                  {metric.value}
                </Typography>
                <Typography
                  component="div"
                  className={classes.statLabel}
                  style={statLabelStyle}
                >
                  {metric.label}
                </Typography>
              </div>
            ))}
          </div>
        ) : null}
      </Paper>
    );
  }
}

MatchPlayerBox.propTypes = {
  classes: PropTypes.object.isRequired,
  player: PropTypes.shape({
    steamId: PropTypes.string,
    userId: PropTypes.string,
    name: PropTypes.string,
    nickname: PropTypes.string,
    picture: PropTypes.string,
    flagImageUrl: PropTypes.string,
    elo: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    kyc: PropTypes.shape({
      badgeEligible: PropTypes.bool,
    }),
    _id: PropTypes.string,
  }).isRequired,
  currentUser: PropTypes.object,
  avatarCacheBust: PropTypes.number,
  relationship: PropTypes.string,
  menuAnchor: PropTypes.any,
  menuAnchorSteamId: PropTypes.string,
  onMenuOpen: PropTypes.func.isRequired,
  onMenuClose: PropTypes.func.isRequired,
  onProfileClick: PropTypes.func.isRequired,
  onAddFriend: PropTypes.func.isRequired,
  onCancelFriendRequest: PropTypes.func,
  onReportClick: PropTypes.func.isRequired,
  canReport: PropTypes.bool,
  alreadyReported: PropTypes.bool,
  viewMode: PropTypes.string,
};

export default withStyles(styles)(MatchPlayerBox);
